import timezones from "@/assets/timezones.json";
import { AddressInput } from "@/components/molecules/address-input";
import { CompanyAvatar } from "@/components/molecules/company-avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { OnboardingDraft } from "@/features/onboarding/types";
import {
  localizedCountries,
  localizedLanguages,
} from "@/features/utils/display-names";
import { useTranslation } from "@/features/utils/i18n";
import { formatTimezoneDisplay, groupTimezones } from "@/lib/timezone-utils";
import { CaretDownIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { OnboardingLayout } from "./layout";

/**
 * The company — the first screen the client sees *after* the analysis, and the
 * first one that is genuinely a review.
 *
 * Nothing here is work: every field arrives filled in and the client is only
 * asked to glance. Language, country and timezone are folded behind a summary
 * line because they are almost always right, and three more selects on screen
 * would make this look like the form the whole flow is trying not to be.
 */
export function StepCompany({
  draft,
  analysisFailed,
  onChange,
  onNext,
  onBack,
  isSubmitting,
}: {
  draft: OnboardingDraft;
  /**
   * The website could not be read, so nothing here was pre-filled from it.
   * The agent says so instead of claiming to have read the site above empty
   * fields.
   */
  analysisFailed?: boolean;
  onChange: (patch: OnboardingDraft) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}) {
  const { t, i18n } = useTranslation("onboarding");
  const languages = useMemo(
    () => localizedLanguages(i18n.language),
    [i18n.language],
  );
  const countries = useMemo(
    () => localizedCountries(i18n.language),
    [i18n.language],
  );
  const [showLocale, setShowLocale] = useState(false);

  const company = draft.company || {};

  const groupedTimezones = useMemo(
    () => groupTimezones(Object.keys(timezones).sort()),
    [],
  );

  const setCompany = (
    patch: Partial<NonNullable<OnboardingDraft["company"]>>,
  ) => onChange({ company: { ...company, ...patch } });

  const localeSummary = [
    languages.find((entry) => entry.code === company.default_language)?.name,
    countries.find((entry) => entry.code === company.default_country)?.name,
    company.default_timezone,
  ]
    .filter(Boolean)
    .join(" · ");

  // The agent introduces itself with this, so it cannot be blank. Everything
  // else on this screen may legitimately be empty.
  const canContinue = Boolean(company.name?.trim());

  return (
    <OnboardingLayout
      step="company"
      title={
        analysisFailed
          ? t("steps.company.introFailed")
          : draft.agent?.name?.trim()
            ? // The first screen after the casting: the agent uses its new name.
              t("steps.company.introNamed", { name: draft.agent.name.trim() })
            : t("steps.company.intro")
      }
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!canContinue}
      isSubmitting={isSubmitting}
    >
      <div className="flex items-center gap-4">
        <CompanyAvatar
          logo={company.logo || null}
          name={company.name || ""}
          size="lg"
        />
        <div className="flex-1 space-y-2">
          <Label htmlFor="company-name">{t("steps.company.name")}</Label>
          <Input
            id="company-name"
            value={company.name || ""}
            onChange={(event) => setCompany({ name: event.target.value })}
            placeholder={t("steps.company.namePlaceholder")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="company-about">{t("steps.company.about")}</Label>
        <Textarea
          id="company-about"
          value={company.about || ""}
          onChange={(event) => setCompany({ about: event.target.value })}
          placeholder={t("steps.company.aboutPlaceholder")}
          rows={4}
        />
        <p className="text-xs text-muted-foreground">
          {t("steps.company.aboutHint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="company-address">{t("steps.company.address")}</Label>
        <AddressInput
          id="company-address"
          name="address"
          value={company.address || ""}
          onChange={(value: string) => setCompany({ address: value })}
          placeholder={t("steps.company.addressPlaceholder")}
          autoVerify
        />
      </div>

      {showLocale ? (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="space-y-2">
            <Label htmlFor="language">{t("steps.company.language")}</Label>
            <Select
              value={company.default_language || ""}
              onValueChange={(value) => setCompany({ default_language: value })}
            >
              <SelectTrigger id="language">
                <SelectValue placeholder={t("steps.company.language")} />
              </SelectTrigger>
              <SelectContent>
                {languages.map((language) => (
                  <SelectItem key={language.code} value={language.code}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t("steps.company.languageHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">{t("steps.company.country")}</Label>
            <Select
              value={company.default_country || ""}
              onValueChange={(value) => setCompany({ default_country: value })}
            >
              <SelectTrigger id="country">
                <SelectValue placeholder={t("steps.company.country")} />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">{t("steps.company.timezone")}</Label>
            <Select
              value={company.default_timezone || ""}
              onValueChange={(value) => setCompany({ default_timezone: value })}
            >
              <SelectTrigger id="timezone">
                <SelectValue placeholder={t("steps.company.timezone")} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] overflow-y-auto">
                {groupedTimezones.map((group) => (
                  <SelectItem key={group.canonical} value={group.canonical}>
                    {formatTimezoneDisplay(group.canonical, group.offset)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowLocale(true)}
          className="flex w-full items-center justify-between rounded-lg border p-3 text-left text-sm hover:bg-slate-500/5"
        >
          <span className="flex flex-col">
            <span className="font-medium">
              {t("steps.company.localeTitle")}
            </span>
            <span className="text-muted-foreground">
              {localeSummary || t("steps.company.localeEmpty")}
            </span>
          </span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <PencilSimpleIcon className="h-4 w-4" />
            <CaretDownIcon className="h-3 w-3" />
          </span>
        </button>
      )}
    </OnboardingLayout>
  );
}
